import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Seo from "@/components/base/Seo";

const contactInfo = [
  {
    icon: "ri-mail-line",
    title: "邮箱",
    value: "hello@fanmugua.example",
  },
  {
    icon: "ri-time-line",
    title: "回复时间",
    value: "工作日 10:00 - 19:00",
  },
  {
    icon: "ri-chat-1-line",
    title: "合作与推荐",
    value: "欢迎推荐新作、反馈建议",
  },
];

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = (data.get("company_alt") as string) || "";

    // anti-spam: silently accept as success
    if (honeypot.trim() !== "") {
      setStatus("success");
      form.reset();
      return;
    }

    data.delete("company_alt");

    try {
      const res = await fetch(
        "https://readdy.ai/api/form/da23a6bk8r5te5mqsfdg",
        {
          method: "POST",
          body: new URLSearchParams(data as unknown as Record<string, string>),
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        }
      );
      const responseText = await res.text();
      let parsed: Record<string, any> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const isSpam =
        !res.ok ||
        (typeof serverMsg === "string" &&
          serverMsg.toLowerCase().includes("spam"));

      if (res.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          serverMsg ||
            "提交失败，请稍后重试，或改用邮箱与我们联系。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <>
      <Seo
        title="联系我们 - 番木瓜漫画"
        description="联系番木瓜漫画：推荐新作、反馈建议或咨询追更问题，欢迎填写联系表单或发送邮件。番木瓜漫画每日更新最新上架与热门新番资讯。"
        keywords="番木瓜漫画,番木瓜漫画联系我们,番木瓜漫画反馈,番木瓜漫画推荐新作"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "联系我们 - 番木瓜漫画",
          },
        ]}
      />
      <Navbar />
      <main className="pt-16">
        <section className="relative overflow-hidden bg-foreground-950">
          <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <nav aria-label="面包屑" className="text-xs text-background-200/70">
              <Link to="/" className="transition hover:text-primary-300">
                首页
              </Link>
              <span className="mx-2">/</span>
              <span className="text-primary-300">联系我们</span>
            </nav>
            <h1 className="mt-3 font-heading text-3xl text-background-50 md:text-4xl">
              联系我们
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-background-100/85 md:text-base">
              想推荐新作、反馈建议，或对番木瓜漫画有任何疑问？欢迎填写下面的表单，我们会尽快回复你。
            </p>
          </div>
        </section>

        <section className="bg-background-50">
          <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-8 px-4 py-10 md:grid-cols-5 md:px-6 md:py-14">
            {/* info */}
            <div className="md:col-span-2">
              <h2 className="font-heading text-2xl text-foreground-950">
                联系方式
              </h2>
              <p className="mt-2 text-sm leading-6 text-foreground-600">
                无论是作品推荐、内容建议还是使用问题，都欢迎与我们沟通。
              </p>
              <div className="mt-6 space-y-4">
                {contactInfo.map((c) => (
                  <div
                    key={c.title}
                    className="flex items-start gap-4 rounded-2xl border border-background-200 bg-background-100/60 p-5"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-500 text-background-50">
                      <i className={`${c.icon} text-lg`}></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-bold text-foreground-900">
                        {c.title}
                      </h3>
                      <p className="mt-1 text-sm text-foreground-600">
                        {c.value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-center gap-3">
                {["ri-weibo-line", "ri-wechat-line", "ri-bilibili-line"].map(
                  (ic) => (
                    <a
                      key={ic}
                      href="#contact"
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-background-300 text-foreground-700 transition hover:bg-primary-500 hover:border-primary-500 hover:text-background-50"
                      aria-label="社交媒体入口"
                    >
                      <i className={`${ic} text-base`}></i>
                    </a>
                  )
                )}
              </div>
            </div>

            {/* form */}
            <div className="md:col-span-3">
              <form
                id="contact-form"
                data-readdy-form="番木瓜漫画联系表单"
                onSubmit={handleSubmit}
                className="rounded-3xl border border-background-200 bg-background-100/60 p-6 md:p-8"
              >
                <h2 className="font-heading text-xl text-foreground-950">
                  给我们留言
                </h2>

                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="flex flex-col">
                    <label
                      htmlFor="name"
                      className="mb-1.5 text-sm font-medium text-foreground-800"
                    >
                      你的称呼
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你"
                      className="rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label
                      htmlFor="email"
                      className="mb-1.5 text-sm font-medium text-foreground-800"
                    >
                      电子邮箱
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="用于接收我们的回复"
                      className="rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                <div className="mt-4 flex flex-col">
                  <label
                    htmlFor="subject"
                    className="mb-1.5 text-sm font-medium text-foreground-800"
                  >
                    留言主题
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    className="rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  >
                    <option value="作品推荐">作品推荐</option>
                    <option value="内容建议">内容建议</option>
                    <option value="问题反馈">问题反馈</option>
                    <option value="合作洽谈">合作洽谈</option>
                    <option value="其他">其他</option>
                  </select>
                </div>

                <div className="mt-4 flex flex-col">
                  <label
                    htmlFor="message"
                    className="mb-1.5 text-sm font-medium text-foreground-800"
                  >
                    留言内容
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="请写下你的想法（最多 500 字）"
                    className="resize-none rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  ></textarea>
                </div>

                {/* honeypot */}
                <input
                  type="text"
                  name="company_alt"
                  autoComplete="off"
                  tabIndex={-1}
                  aria-hidden="true"
                  readOnly
                  className="hp-field"
                />

                <button
                  type="submit"
                  className="mt-6 flex w-full items-center justify-center rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition hover:bg-primary-600 sm:w-auto whitespace-nowrap"
                >
                  提交留言
                  <i className="ri-send-plane-line ml-2"></i>
                </button>

                {status === "success" && (
                  <p className="mt-4 flex items-center gap-2 rounded-xl bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                    <i className="ri-checkbox-circle-line text-lg"></i>
                    提交成功！感谢你的留言，我们会尽快与你联系。
                  </p>
                )}
                {status === "error" && (
                  <p className="mt-4 flex items-center gap-2 rounded-xl bg-accent-100 px-4 py-3 text-sm text-accent-900">
                    <i className="ri-error-warning-line text-lg"></i>
                    {formError}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}